import { useState, useEffect } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import {
  FileText, Save, RotateCcw, CheckCircle2, History, Terminal, Plus, Trash2,
  Eye, EyeOff, Play, ScrollText, Power, RefreshCw, Server, ShieldAlert,
  Boxes, Square, RotateCw, DownloadCloud, Rocket, Wifi, Activity,
} from "lucide-react";

const SECRET_KW = /TOKEN|SECRET|PASSWORD|KEY|JWT/i;

const envelopeText = (res) => (!res ? "" : res.ok ? (typeof res.data === "string" ? res.data : (res.data?.content ?? res.data?.raw ?? JSON.stringify(res.data, null, 2))) : (res.error || "Indisponible"));

// ---------------- Files (generic whitelisted editor) ----------------
export function FilesTab({ askConfirm, reload }) {
  const [files, setFiles] = useState([]);
  const [path, setPath] = useState("");
  const [orig, setOrig] = useState("");
  const [content, setContent] = useState("");
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/infrastructure/files").then((r) => setFiles(r.data.files || [])).catch(() => {}); }, []);

  const open = async (p) => {
    setPath(p); setLoading(true);
    try {
      const { data } = await api.get(`/infrastructure/files/read`, { params: { path: p } });
      const txt = data.ok ? (data.data?.content ?? envelopeText(data)) : "";
      setContent(txt); setOrig(txt);
      if (!data.ok) toast.error(data.error || "Lecture impossible");
      const b = await api.get(`/infrastructure/files/backups`, { params: { path: p } }).then((r) => r.data).catch(() => null);
      setBackups(b?.ok ? (b.data?.backups || b.data || []) : []);
    } finally { setLoading(false); reload?.(); }
  };

  const changed = content !== orig;
  const validate = async () => {
    const { data } = await api.post(`/infrastructure/files/validate`, { path, content });
    if (data.ok) toast.success("Configuration valide"); else toast.error(data.error || "Validation échouée");
  };
  const save = () => askConfirm({
    title: "Confirmer l'écriture du fichier", critical: true,
    message: `Écrire ${path} ? Une sauvegarde horodatée est créée automatiquement avant modification.`,
    onYes: async () => {
      const { data } = await api.post(`/infrastructure/files/write`, { path, content, reason: "Édition via SkyCore" });
      if (data.ok) { toast.success("Fichier sauvegardé"); setOrig(content); open(path); }
      else toast.error(data.error || "Écriture refusée");
      reload?.();
    },
  });
  const restore = (bk) => askConfirm({
    title: "Restaurer une sauvegarde", critical: true,
    message: `Restaurer ${path} depuis ${bk} ? Le contenu actuel sera remplacé.`,
    onYes: async () => { const { data } = await api.post(`/infrastructure/files/restore`, { path, backup: bk }); if (data.ok) { toast.success("Sauvegarde restaurée"); open(path); } else toast.error(data.error || "Restauration échouée"); reload?.(); },
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="files-tab">
      <div className="bg-[#141414] border border-white/10 rounded-sm p-2 space-y-1">
        <div className="font-mono text-[9px] uppercase tracking-wider text-[#525252] px-1 py-1">Fichiers autorisés</div>
        {files.map((f) => (
          <button key={f} data-testid={`file-${f}`} onClick={() => open(f)}
            className={`w-full text-left font-mono text-[10px] px-2 py-1.5 rounded-sm truncate ${path === f ? "bg-[#3B82F6]/20 text-white" : "text-[#A3A3A3] hover:bg-white/5"}`}>
            <FileText size={11} className="inline mr-1" />{f}
          </button>
        ))}
      </div>
      <div className="lg:col-span-3 space-y-2">
        {!path ? <div className="font-mono text-[11px] text-[#525252]">Sélectionnez un fichier à éditer.</div> : (
          <>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[11px] text-white">{path}</span>
              {changed && <span className="font-mono text-[9px] text-[#F59E0B] border border-[#F59E0B]/40 rounded px-1.5 py-0.5">Modifié</span>}
              <div className="ml-auto flex items-center gap-1.5">
                <button data-testid="file-validate" onClick={validate} className="flex items-center gap-1 font-mono text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1.5 hover:bg-white/10"><CheckCircle2 size={11} /> Valider</button>
                <button data-testid="file-save" onClick={save} disabled={!changed} className="flex items-center gap-1 font-mono text-[9px] uppercase bg-[#3B82F6] rounded-sm px-2 py-1.5 disabled:opacity-40"><Save size={11} /> Sauvegarder</button>
              </div>
            </div>
            <textarea data-testid="file-editor" value={content} onChange={(e) => setContent(e.target.value)} spellCheck={false}
              className="w-full h-80 bg-[#0A0A0A] border border-white/10 rounded-sm p-3 font-mono text-[11px] text-[#A3A3A3] outline-none" disabled={loading} />
            <div className="bg-[#141414] border border-white/10 rounded-sm p-2">
              <div className="font-mono text-[9px] uppercase tracking-wider text-[#525252] mb-1 flex items-center gap-1"><History size={11} /> Sauvegardes</div>
              {(!backups || backups.length === 0) ? <div className="font-mono text-[10px] text-[#525252]">Aucune sauvegarde disponible</div> :
                backups.map((b, i) => {
                  const name = typeof b === "string" ? b : (b.path || b.name || JSON.stringify(b));
                  return <div key={i} className="flex items-center gap-2 font-mono text-[10px] py-0.5"><span className="text-[#A3A3A3] truncate">{name}</span>
                    <button data-testid={`file-restore-${i}`} onClick={() => restore(name)} className="ml-auto text-[#F59E0B] hover:underline"><RotateCcw size={11} className="inline" /> Restaurer</button></div>;
                })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------- Env editor (table, masked secrets) ----------------
const ENV_FILES = ["/opt/infra-api/.env", "/opt/drone-gateway/.env"];
export function EnvTab({ askConfirm, reload }) {
  const [path, setPath] = useState(ENV_FILES[0]);
  const [rows, setRows] = useState([]);
  const [reveal, setReveal] = useState(false);

  const load = async (p) => {
    const { data } = await api.get(`/infrastructure/files/read`, { params: { path: p } });
    const txt = data.ok ? (data.data?.content ?? "") : "";
    if (!data.ok) toast.error(data.error || "Lecture impossible");
    const parsed = txt.split("\n").filter((l) => l.trim() && !l.trim().startsWith("#")).map((l) => { const i = l.indexOf("="); return { key: l.slice(0, i).trim(), value: l.slice(i + 1) }; });
    setRows(parsed); reload?.();
  };
  useEffect(() => { load(path); }, [path]); // eslint-disable-line

  const setVal = (i, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, value: v } : x)));
  const setKey = (i, k) => setRows((r) => r.map((x, j) => (j === i ? { ...x, key: k } : x)));
  const del = (i) => setRows((r) => r.filter((_, j) => j !== i));
  const add = () => setRows((r) => [...r, { key: "", value: "" }]);
  const doReveal = () => askConfirm({ title: "Révéler les secrets", message: "Afficher les valeurs sensibles en clair ?", onYes: () => setReveal(true) });

  const save = () => askConfirm({
    title: "Enregistrer les variables", critical: true,
    message: `Écrire ${path} ? Une sauvegarde est créée. Les secrets ne sont pas journalisés.`,
    onYes: async () => {
      const content = rows.filter((r) => r.key).map((r) => `${r.key}=${r.value}`).join("\n") + "\n";
      const { data } = await api.post(`/infrastructure/files/write`, { path, content, reason: "Édition .env via SkyCore" });
      if (data.ok) toast.success("Variables enregistrées"); else toast.error(data.error || "Écriture refusée");
      reload?.();
    },
  });

  return (
    <div className="space-y-3" data-testid="env-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <select data-testid="env-file" value={path} onChange={(e) => setPath(e.target.value)} className="bg-[#141414] border border-white/15 rounded-sm font-mono text-[11px] px-2 py-2 text-white">
          {ENV_FILES.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <button data-testid="env-reveal" onClick={reveal ? () => setReveal(false) : doReveal} className="flex items-center gap-1 font-mono text-[10px] uppercase border border-white/15 rounded-sm px-2 py-2 hover:bg-white/10">{reveal ? <EyeOff size={12} /> : <Eye size={12} />} {reveal ? "Masquer" : "Révéler"}</button>
        <button data-testid="env-add" onClick={add} className="flex items-center gap-1 font-mono text-[10px] uppercase border border-white/15 rounded-sm px-2 py-2 hover:bg-white/10"><Plus size={12} /> Variable</button>
        <button data-testid="env-save" onClick={save} className="ml-auto flex items-center gap-1 font-mono text-[10px] uppercase bg-[#3B82F6] rounded-sm px-3 py-2"><Save size={12} /> Enregistrer</button>
      </div>
      <div className="bg-[#141414] border border-white/10 rounded-sm overflow-hidden" data-testid="env-table">
        {rows.map((r, i) => {
          const secret = SECRET_KW.test(r.key);
          return (
            <div key={i} className="flex items-center gap-2 px-2 py-1 border-b border-white/5">
              <input value={r.key} onChange={(e) => setKey(i, e.target.value)} className="bg-[#0A0A0A] border border-white/10 rounded-sm font-mono text-[10px] px-2 py-1 text-white w-56" />
              <input type={secret && !reveal ? "password" : "text"} value={secret && !reveal ? "••••••••" : r.value} onChange={(e) => setVal(i, e.target.value)} disabled={secret && !reveal}
                className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-sm font-mono text-[10px] px-2 py-1 text-[#A3A3A3]" />
              {secret && <span className="font-mono text-[8px] text-[#F59E0B] uppercase">secret</span>}
              <button onClick={() => del(i)} className="text-[#EF4444]/70 hover:text-[#EF4444]"><Trash2 size={12} /></button>
            </div>
          );
        })}
        {rows.length === 0 && <div className="p-3 font-mono text-[10px] text-[#525252]">Aucune variable</div>}
      </div>
    </div>
  );
}

// ---------------- Docker Compose ----------------
export function ComposeTab({ askConfirm, reload }) {
  const path = "/opt/drone-gateway/docker-compose.yml";
  const [orig, setOrig] = useState(""); const [content, setContent] = useState("");
  const load = async () => { const { data } = await api.get(`/infrastructure/files/read`, { params: { path } }); const t = data.ok ? (data.data?.content ?? "") : ""; setContent(t); setOrig(t); if (!data.ok) toast.error(data.error || "Lecture impossible"); reload?.(); };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const validate = async () => { const { data } = await api.post(`/infrastructure/files/validate`, { path, content }); data.ok ? toast.success("docker compose config OK") : toast.error(data.error || "Validation échouée"); };
  const save = () => askConfirm({ title: "Enregistrer docker-compose.yml", critical: true, message: "Sauvegarder le fichier (backup automatique) ?", onYes: async () => { const { data } = await api.post(`/infrastructure/files/write`, { path, content, reason: "Édition compose" }); data.ok ? (toast.success("Enregistré"), setOrig(content)) : toast.error(data.error || "Écriture refusée"); reload?.(); } });
  const apply = () => askConfirm({ title: "Appliquer (compose up -d)", critical: true, message: "Appliquer la configuration via docker compose up -d ? Cela peut redémarrer les conteneurs.", onYes: async () => { const { data } = await api.post(`/infrastructure/docker/compose/up`); data.ok ? toast.success("Compose appliqué") : toast.error(data.error || "Échec compose"); reload?.(); } });
  return (
    <div className="space-y-2" data-testid="compose-tab">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] text-white">{path}</span>
        <div className="ml-auto flex gap-1.5">
          <button data-testid="compose-validate" onClick={validate} className="flex items-center gap-1 font-mono text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1.5 hover:bg-white/10"><CheckCircle2 size={11} /> Valider</button>
          <button data-testid="compose-save" onClick={save} disabled={content === orig} className="flex items-center gap-1 font-mono text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1.5 disabled:opacity-40"><Save size={11} /> Sauvegarder</button>
          <button data-testid="compose-apply" onClick={apply} className="flex items-center gap-1 font-mono text-[9px] uppercase bg-[#F59E0B] text-black rounded-sm px-2 py-1.5"><Play size={11} /> Appliquer</button>
        </div>
      </div>
      <textarea data-testid="compose-editor" value={content} onChange={(e) => setContent(e.target.value)} spellCheck={false} className="w-full h-96 bg-[#0A0A0A] border border-white/10 rounded-sm p-3 font-mono text-[11px] text-[#A3A3A3] outline-none" />
    </div>
  );
}

// ---------------- Secure shell ----------------
export function ShellTab({ askConfirm, reload }) {
  const [presets, setPresets] = useState([]);
  const [cmd, setCmd] = useState("");
  const [out, setOut] = useState(null);
  const [hist, setHist] = useState([]);
  useEffect(() => { api.get("/infrastructure/shell/presets").then((r) => setPresets(r.data.presets || [])).catch(() => {}); }, []);
  const run = async (command, custom) => {
    const exec = async () => {
      try { const { data } = await api.post("/infrastructure/shell/run", { command, reason: "Diagnostic" }); setOut(data); setHist((h) => [command, ...h].slice(0, 15)); if (!data.ok) toast.error(data.error || "Commande échouée"); }
      catch (e) { setOut({ ok: false, error: apiErr(e) }); toast.error(apiErr(e, "Commande refusée")); }
      reload?.();
    };
    if (custom) askConfirm({ title: "Exécuter une commande", critical: true, message: `Exécuter : "${command}" ? Les commandes destructrices sont bloquées.`, onYes: exec });
    else exec();
  };
  const d = out?.ok ? out.data : null;
  return (
    <div className="space-y-3" data-testid="shell-tab">
      <div className="flex items-center gap-2 font-mono text-[10px] text-[#F59E0B] uppercase"><ShieldAlert size={13} /> Terminal sécurisé — action sensible, sortie limitée à 5000 caractères</div>
      <div className="flex flex-wrap gap-1">
        {presets.map((p) => <button key={p} data-testid={`preset-${p.replace(/[^a-z0-9]+/gi, "-")}`} onClick={() => run(p, false)} className="font-mono text-[9px] border border-white/15 rounded-sm px-2 py-1 text-[#A3A3A3] hover:bg-white/10">{p}</button>)}
      </div>
      <div className="flex items-center gap-2">
        <Terminal size={14} className="text-[#3B82F6]" />
        <input data-testid="shell-input" value={cmd} onChange={(e) => setCmd(e.target.value)} placeholder="Commande contrôlée..." className="flex-1 bg-[#0A0A0A] border border-white/10 rounded-sm font-mono text-[11px] px-2 py-2 text-white outline-none" />
        <button data-testid="shell-run" onClick={() => cmd.trim() && run(cmd.trim(), true)} className="font-mono text-[10px] uppercase bg-[#3B82F6] rounded-sm px-3 py-2"><Play size={12} className="inline" /> Exécuter</button>
      </div>
      <pre data-testid="shell-output" className="bg-[#0A0A0A] border border-white/10 rounded-sm p-3 font-mono text-[10px] text-[#A3A3A3] whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
        {out == null ? "Sélectionnez une commande rapide ou saisissez une commande contrôlée." :
          out.ok ? `# returncode: ${d?.returncode ?? "?"}  (${d?.duration_ms ?? "?"} ms)\n${d?.stdout || ""}${d?.stderr ? "\n[stderr]\n" + d.stderr : ""}` :
          `Erreur : ${out.error}`}
      </pre>
      {hist.length > 0 && <div className="font-mono text-[9px] text-[#525252]">Historique : {hist.map((h, i) => <span key={i} className="text-[#A3A3A3]">{h}{i < hist.length - 1 ? " · " : ""}</span>)}</div>}
    </div>
  );
}

// ---------------- Raspberry nodes ----------------
export function RaspberryTab({ askConfirm, reload }) {
  const [nodes, setNodes] = useState([]);
  const [logs, setLogs] = useState({});
  const load = () => api.get("/infrastructure/raspberries").then((r) => setNodes(r.data.raspberries || [])).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);
  const status = async (n) => { const { data } = await api.get(`/infrastructure/raspberries/${n.id}/status`); load(); if (!data.ok) toast.error(data.error || "Hors ligne"); else toast.success("Statut mis à jour"); reload?.(); };
  const showLogs = async (n) => { const { data } = await api.get(`/infrastructure/raspberries/${n.id}/logs`); setLogs((l) => ({ ...l, [n.id]: data.ok ? (typeof data.data === "string" ? data.data : (data.data?.logs ?? JSON.stringify(data.data, null, 2))) : data.error })); reload?.(); };
  const restartSvc = (n) => askConfirm({ title: "Redémarrer skycore-drone", message: `Redémarrer le service sur ${n.drone_id} ?`, onYes: async () => { const { data } = await api.post(`/infrastructure/raspberries/${n.id}/restart-service`); data.ok ? toast.success("Service redémarré") : toast.error(data.error || "Échec"); reload?.(); } });
  const reboot = (n) => askConfirm({ title: "Redémarrer le Raspberry", critical: true, message: `Redémarrer (reboot) le Raspberry de ${n.drone_id} ? Le drone sera hors ligne quelques minutes.`, onYes: async () => { const { data } = await api.post(`/infrastructure/raspberries/${n.id}/reboot`); data.ok ? toast.success("Reboot demandé") : toast.error(data.error || "Échec"); reload?.(); } });
  const col = (s) => ({ online: "#22C55E", degraded: "#F59E0B", link_only: "#3B82F6", disabled: "#525252", offline: "#EF4444" }[s] || "#EF4444");
  const lbl = (s) => ({ online: "EN LIGNE", degraded: "DÉGRADÉ", link_only: "LIAISON", disabled: "DÉSACTIVÉ", offline: "HORS LIGNE" }[s] || (s || "—").toUpperCase());
  const verBadge = (s) => ({ up_to_date: { label: "À jour", color: "#22C55E" }, update_available: { label: "MAJ disponible", color: "#F59E0B" } }[s] || { label: "Version inconnue", color: "#525252" });
  const [showHist, setShowHist] = useState({});
  const toggleHist = (id) => setShowHist((p) => ({ ...p, [id]: !p[id] }));
  const fmtSeen = (t) => (t ? new Date(t).toLocaleTimeString("fr-FR") : "jamais");

  const [busy, setBusy] = useState("");
  const [report, setReport] = useState({});
  const setRep = (id, r) => setReport((p) => ({ ...p, [id]: r }));

  const provision = (n, update) => askConfirm({
    title: update ? "Mettre à jour l'agent Raspberry" : "Installer l'agent Raspberry",
    critical: true,
    message: `${update ? "Mettre à jour" : "Installer"} l'agent SkyCore sur ${n.drone_id} (WG ${n.wireguard_ip}) via SSH ? Une sauvegarde de l'agent existant sera créée avant remplacement.`,
    onYes: async () => {
      setBusy(`prov:${n.id}`);
      try {
        const { data } = await api.post(`/infrastructure/raspberries/${n.id}/provision-agent`, { restart: true });
        setRep(n.id, data);
        (data.ok && (data.data?.ok ?? true)) ? toast.success("Agent provisionné") : toast.error(data.error || data.data?.error || "Échec provisioning");
      } catch (e) { toast.error(apiErr(e, "Échec provisioning")); }
      finally { setBusy(""); load(); reload?.(); }
    },
  });

  const restartAgent = (n) => askConfirm({
    title: "Redémarrer l'agent", message: `Redémarrer le service skycore-drone-agent sur ${n.drone_id} ?`,
    onYes: async () => {
      setBusy(`ra:${n.id}`);
      try { const { data } = await api.post(`/infrastructure/raspberries/${n.id}/restart-agent`); data.ok ? toast.success("Agent redémarré") : toast.error(data.error || "Échec"); }
      catch (e) { toast.error(apiErr(e, "Échec")); } finally { setBusy(""); }
    },
  });

  const agentLogs = async (n) => {
    setBusy(`al:${n.id}`);
    try { const { data } = await api.get(`/infrastructure/raspberries/${n.id}/agent-logs`); setLogs((l) => ({ ...l, [n.id]: data.ok ? (data.data?.logs ?? JSON.stringify(data.data, null, 2)) : data.error })); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const testTelemetry = async (n) => {
    setBusy(`tt:${n.id}`);
    try { const { data } = await api.get(`/infrastructure/raspberries/${n.id}/test-telemetry`); data.ok ? toast.success(`Télémétrie OK · ${data.gateway_status?.status}`) : toast.error("Aucune télémétrie live (offline)"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const testGateway = async (n) => {
    setBusy(`tg:${n.id}`);
    try { const { data } = await api.get(`/infrastructure/raspberries/${n.id}/test-gateway`); data.ok ? toast.success("Gateway Hetzner en ligne") : toast.error("Gateway hors ligne"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-3" data-testid="raspberry-tab">
      <div className="flex items-center gap-2 font-mono text-[10px] text-[#A3A3A3] bg-[#141414] border border-white/10 rounded-sm px-3 py-2">
        <Server size={13} className="text-[#3B82F6]" /> Vue technique des drones (source unique). Les drones sont enregistrés dans
        <span className="text-white">&nbsp;Drones &amp; Tokens</span>&nbsp;ou via enrôlement automatique (heartbeat). Statut calculé en temps réel.
      </div>
      <div className="space-y-2" data-testid="rpi-list">
        {nodes.length === 0 && <div className="font-mono text-[11px] text-[#525252]">Aucun drone enregistré. Créez-en un dans « Drones &amp; Tokens ».</div>}
        {nodes.map((n) => (
          <div key={n.id} className="bg-[#141414] border border-white/10 rounded-sm p-3 space-y-2">
            <div className="flex items-center gap-2 flex-wrap font-mono text-[11px]">
              <span className="relative flex h-2 w-2"><span className="inline-flex h-2 w-2 rounded-full" style={{ backgroundColor: col(n.status) }} /></span>
              <span className="text-white">{n.drone_id}</span>
              <span className="text-[#A3A3A3]">{n.hostname || "—"}</span>
              <span className="text-[#525252]">WG {n.wireguard_ip || "—"} · LAN {n.lan_ip || "—"}</span>
              {n.software_version && <span className="text-[#525252]">v{n.software_version}</span>}
              <span data-testid={`rpi-agent-version-${n.drone_id}`} className="uppercase text-[8px] font-bold px-1 rounded-sm" style={{ color: verBadge(n.agent_update_status).color, border: `1px solid ${verBadge(n.agent_update_status).color}33` }} title={n.latest_available_version ? `Référence v${n.latest_available_version}` : ""}>{verBadge(n.agent_update_status).label}</span>
              <span data-testid={`rpi-status-badge-${n.drone_id}`} className="uppercase text-[9px] font-bold" style={{ color: col(n.status) }}>{lbl(n.status)}</span>
              <span data-testid={`rpi-admin-badge-${n.drone_id}`} className="uppercase text-[8px] font-bold px-1 rounded-sm" style={{ color: n.active ? "#22C55E" : "#EF4444", border: `1px solid ${n.active ? "#22C55E" : "#EF4444"}33` }}>{n.active ? "actif" : "inactif"}</span>
              <span className="text-[#525252] text-[9px]">vu: {fmtSeen(n.last_seen)}</span>
              <div className="ml-auto flex gap-1.5">
                <button data-testid={`rpi-status-${n.drone_id}`} onClick={() => status(n)} className="flex items-center gap-1 text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10"><RefreshCw size={10} /> Statut</button>
                <button data-testid={`rpi-logs-${n.drone_id}`} onClick={() => showLogs(n)} className="flex items-center gap-1 text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10"><ScrollText size={10} /> Logs</button>
                <button data-testid={`rpi-restart-${n.drone_id}`} onClick={() => restartSvc(n)} className="flex items-center gap-1 text-[9px] uppercase border border-[#F59E0B]/40 text-[#F59E0B] rounded-sm px-2 py-1 hover:bg-[#F59E0B]/10"><RefreshCw size={10} /> Service</button>
                <button data-testid={`rpi-reboot-${n.drone_id}`} onClick={() => reboot(n)} className="flex items-center gap-1 text-[9px] uppercase border border-[#EF4444]/40 text-[#EF4444] rounded-sm px-2 py-1 hover:bg-[#EF4444]/10"><Power size={10} /> Reboot</button>
              </div>
            </div>
            {/* Provisioning automatique de l'agent (SSH via Infra API Hetzner) */}
            <div className="flex flex-wrap items-center gap-1.5 pt-1.5 border-t border-white/5">
              <span className="font-mono text-[8px] uppercase tracking-wider text-[#525252] mr-1">Agent :</span>
              <button data-testid={`rpi-provision-${n.drone_id}`} disabled={busy === `prov:${n.id}`} onClick={() => provision(n, !!n.last_seen)}
                className="flex items-center gap-1 text-[9px] uppercase border border-[#3B82F6]/50 text-[#3B82F6] rounded-sm px-2 py-1 hover:bg-[#3B82F6]/10 disabled:opacity-40">
                {busy === `prov:${n.id}` ? <RefreshCw size={10} className="animate-spin" /> : <DownloadCloud size={10} />} {n.last_seen ? "MAJ agent" : "Installer agent"}
              </button>
              <button data-testid={`rpi-restart-agent-${n.drone_id}`} onClick={() => restartAgent(n)} className="flex items-center gap-1 text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10"><RotateCw size={10} /> Redémarrer agent</button>
              <button data-testid={`rpi-agent-logs-${n.drone_id}`} onClick={() => agentLogs(n)} className="flex items-center gap-1 text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10"><ScrollText size={10} /> Logs agent</button>
              <button data-testid={`rpi-test-telemetry-${n.drone_id}`} onClick={() => testTelemetry(n)} className="flex items-center gap-1 text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10"><Activity size={10} /> Tester télémétrie</button>
              <button data-testid={`rpi-test-gateway-${n.drone_id}`} onClick={() => testGateway(n)} className="flex items-center gap-1 text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10"><Wifi size={10} /> Tester Gateway</button>
              <button data-testid={`rpi-check-version-${n.drone_id}`} onClick={() => status(n)} className="flex items-center gap-1 text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10"><RefreshCw size={10} /> Vérifier version</button>
              <button data-testid={`rpi-versions-${n.drone_id}`} onClick={() => toggleHist(n.id)} className="flex items-center gap-1 text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10"><ScrollText size={10} /> Versions ({(n.agent_version_history || []).length})</button>
            </div>
            {showHist[n.id] && (
              <div data-testid={`rpi-version-history-${n.drone_id}`} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-2 space-y-1">
                <div className="font-mono text-[9px] uppercase tracking-wider text-[#737373]">Historique des versions agent · référence v{n.latest_available_version || "—"}</div>
                {(n.agent_version_history || []).length === 0 && <div className="font-mono text-[9px] text-[#525252]">Aucun changement de version enregistré.</div>}
                {(n.agent_version_history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="font-mono text-[9px] flex items-center gap-2 text-[#A3A3A3]">
                    <span className="text-white">v{h.version}</span>
                    {h.previous && <span className="text-[#525252]">← v{h.previous}</span>}
                    <span className="text-[#525252] ml-auto">{fmtSeen(h.seen_at)}</span>
                  </div>
                ))}
              </div>
            )}
            {report[n.id] && (
              <div data-testid={`rpi-report-${n.drone_id}`} className="bg-[#0A0A0A] border border-white/10 rounded-sm p-2 space-y-1">
                <div className="font-mono text-[9px] uppercase tracking-wider text-[#737373] flex items-center gap-1">
                  <Rocket size={10} className="text-[#3B82F6]" /> Rapport d'installation
                  <span className={`ml-1 ${(report[n.id].data?.ok ?? report[n.id].ok) ? "text-[#22C55E]" : "text-[#EF4444]"}`}>
                    {(report[n.id].data?.ok ?? report[n.id].ok) ? "OK" : "ÉCHEC"}
                  </span>
                </div>
                {report[n.id].error && <div className="font-mono text-[9px] text-[#EF4444]">{report[n.id].error}</div>}
                {Array.isArray(report[n.id].data?.report) && report[n.id].data.report.map((s, i) => (
                  <div key={i} className="font-mono text-[9px] flex items-start gap-1.5">
                    <span style={{ color: s.ok ? "#22C55E" : "#EF4444" }}>{s.ok ? "✓" : "✗"}</span>
                    <span className="text-[#A3A3A3]">{s.step}</span>
                  </div>
                ))}
                {report[n.id].data?.backup_path && <div className="font-mono text-[8px] text-[#525252]">backup: {report[n.id].data.backup_path}</div>}
              </div>
            )}
            {logs[n.id] && <pre className="bg-[#0A0A0A] border border-white/10 rounded-sm p-2 font-mono text-[9px] text-[#A3A3A3] whitespace-pre-wrap break-words max-h-48 overflow-y-auto">{logs[n.id]}</pre>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Docker management (whitelisted containers) ----------------
const DOCKER_CRITICAL = ["drone_postgres", "drone_redis", "infra-api"];
const DOCKER_IMPACT = {
  drone_api: "Coupe l'API drone (commandes/télémétrie indisponibles).",
  "drone-gateway": "Coupe la passerelle drones (toute la flotte hors ligne).",
  "infra-api": "Coupe l'API d'infrastructure (cette page perd la supervision).",
  drone_postgres: "Base de données : risque de perte de connexion / persistance.",
  drone_redis: "Cache/queues : dégradation temps réel possible.",
};

function normalizeContainers(data) {
  let rows = [];
  if (Array.isArray(data)) rows = data;
  else if (Array.isArray(data?.containers)) rows = data.containers;
  return rows.map((c) => ({
    name: c.name || c.Names || c.Name || "—",
    status: c.status || c.Status || c.State || "—",
    state: (c.state || c.State || c.status || c.Status || "").toString().toLowerCase(),
    ports: c.ports || c.Ports || "—",
    uptime: c.uptime || c.RunningFor || "",
  }));
}

export function DockerManageTab({ askConfirm, reload }) {
  const [res, setRes] = useState(null);
  const [logs, setLogs] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/infrastructure/docker/containers").then((r) => setRes(r.data)).catch((e) => setRes({ ok: false, error: apiErr(e) }));
  useEffect(() => { load(); }, []);

  const act = (name, action) => {
    const impact = DOCKER_IMPACT[name] || "Action sur le conteneur.";
    const doIt = async () => {
      setBusy(`${name}:${action}`);
      try {
        const { data } = await api.post(`/infrastructure/docker/containers/${name}/${action}`);
        if (data.ok) toast.success(`${action} · ${name} demandé`);
        else toast.error(data.error || "Action refusée");
      } catch (e) { toast.error(apiErr(e, "Action refusée")); }
      finally { setBusy(""); load(); reload?.(); }
    };
    const critical = DOCKER_CRITICAL.includes(name);
    const needsDouble = action === "stop" || (action === "restart" && critical);
    if (needsDouble) {
      askConfirm({
        title: `${action === "stop" ? "Arrêter" : "Redémarrer"} ${name}`, critical: true,
        message: `Action sensible sur « ${name} ». ${impact} Confirmer une première fois ?`,
        onYes: () => askConfirm({
          title: "Confirmation finale", critical: true,
          message: `Dernière confirmation : ${action === "stop" ? "ARRÊTER" : "REDÉMARRER"} le conteneur « ${name} » ?`,
          onYes: doIt,
        }),
      });
    } else {
      askConfirm({
        title: `${action === "start" ? "Démarrer" : "Redémarrer"} ${name}`, critical: action !== "start",
        message: `${action === "start" ? "Démarrer" : "Redémarrer"} le conteneur « ${name} » ? ${action === "restart" ? impact : ""}`,
        onYes: doIt,
      });
    }
  };

  const showLogs = async (name) => {
    try {
      const { data } = await api.get(`/infrastructure/docker/containers/${name}/logs`);
      const txt = data.ok ? (typeof data.data === "string" ? data.data : (data.data?.logs ?? JSON.stringify(data.data, null, 2))) : (data.error || "Indisponible");
      setLogs((l) => ({ ...l, [name]: txt }));
    } catch (e) { setLogs((l) => ({ ...l, [name]: apiErr(e) })); }
    reload?.();
  };

  if (res == null) return <div className="font-mono text-xs text-[#525252]">Chargement...</div>;
  if (!res.ok) return <div className="font-mono text-[11px] text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-sm px-3 py-2" data-testid="docker-manage-error">{res.error || "Conteneurs indisponibles"}</div>;
  const list = normalizeContainers(res.data);

  return (
    <div className="space-y-3" data-testid="docker-manage-tab">
      <div className="flex items-center gap-2 font-mono text-[10px] text-[#F59E0B] uppercase">
        <ShieldAlert size={13} /> Gestion conteneurs (whitelist) — start / stop / restart uniquement, pas de suppression
        <button data-testid="docker-reload" onClick={load} className="ml-auto flex items-center gap-1 border border-white/15 rounded-sm px-2 py-1 text-[#A3A3A3] hover:bg-white/10 normal-case"><RefreshCw size={11} /> Rafraîchir</button>
      </div>
      {list.length === 0 && <div className="font-mono text-[11px] text-[#525252]">Aucun conteneur autorisé détecté.</div>}
      <div className="space-y-2" data-testid="docker-containers">
        {list.map((c) => {
          const running = /up|running|healthy/i.test(c.state);
          const critical = DOCKER_CRITICAL.includes(c.name);
          return (
            <div key={c.name} className="bg-[#141414] border border-white/10 rounded-sm p-3 space-y-2">
              <div className="flex items-center gap-2 flex-wrap font-mono text-[11px]">
                <Boxes size={14} style={{ color: running ? "#22C55E" : "#EF4444" }} />
                <span className="text-white">{c.name}</span>
                {critical && <span className="font-mono text-[8px] text-[#F59E0B] border border-[#F59E0B]/40 rounded px-1 uppercase">critique</span>}
                <span className="text-[#A3A3A3]">{c.status}</span>
                <span className="text-[#525252]">{typeof c.ports === "string" ? c.ports : JSON.stringify(c.ports)}</span>
                <div className="ml-auto flex gap-1.5">
                  <button data-testid={`docker-start-${c.name}`} disabled={running || busy} onClick={() => act(c.name, "start")} className="flex items-center gap-1 text-[9px] uppercase border border-[#22C55E]/40 text-[#22C55E] rounded-sm px-2 py-1 hover:bg-[#22C55E]/10 disabled:opacity-30"><Play size={10} /> Start</button>
                  <button data-testid={`docker-restart-${c.name}`} disabled={busy} onClick={() => act(c.name, "restart")} className="flex items-center gap-1 text-[9px] uppercase border border-[#F59E0B]/40 text-[#F59E0B] rounded-sm px-2 py-1 hover:bg-[#F59E0B]/10 disabled:opacity-30"><RotateCw size={10} /> Restart</button>
                  <button data-testid={`docker-stop-${c.name}`} disabled={!running || busy} onClick={() => act(c.name, "stop")} className="flex items-center gap-1 text-[9px] uppercase border border-[#EF4444]/40 text-[#EF4444] rounded-sm px-2 py-1 hover:bg-[#EF4444]/10 disabled:opacity-30"><Square size={10} /> Stop</button>
                  <button data-testid={`docker-logs-${c.name}`} disabled={busy} onClick={() => showLogs(c.name)} className="flex items-center gap-1 text-[9px] uppercase border border-white/15 rounded-sm px-2 py-1 hover:bg-white/10 disabled:opacity-30"><ScrollText size={10} /> Logs</button>
                </div>
              </div>
              {logs[c.name] && <pre className="bg-[#0A0A0A] border border-white/10 rounded-sm p-2 font-mono text-[9px] text-[#A3A3A3] whitespace-pre-wrap break-words max-h-48 overflow-y-auto">{logs[c.name]}</pre>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
